import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  ChevronUp,
  ChevronDown,
  X,
  Trash2,
  Plus,
  Music,
  FileText,
  GripVertical,
  ListPlus,
  Copy,
  Pencil,
  Check,
  Clock,
  Download,
} from "lucide-react";
import { CompletenessRing } from "./CompletenessRing";
import { useAudioDurations, formatDuration, resolveSongDuration } from "../lib/audioDurations";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableSetlistItem = ({
  s,
  index,
  total,
  durationSec,
  isManualDuration,
  onJump,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: s.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-black border rounded-xl p-3 flex items-center gap-2 ${
        isDragging ? "border-[var(--gl-gold)] shadow-lg shadow-[var(--gl-gold)]/30" : "border-white/10"
      }`}
      data-testid={`setlist-item-${s.id}`}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="w-7 h-9 -ml-1 flex items-center justify-center text-neutral-500 hover:text-[var(--gl-gold)] touch-none cursor-grab active:cursor-grabbing shrink-0"
        data-testid={`setlist-drag-${s.id}`}
        aria-label="Glisser pour réordonner"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} />
      </button>

      <span className="text-[var(--gl-gold)] font-display text-lg w-6 text-center tabular-nums">
        {index + 1}
      </span>
      <CompletenessRing song={s} size={14} />
      <button
        onClick={() => onJump?.(s.id)}
        className="flex-1 text-left min-w-0"
        data-testid={`setlist-jump-${s.id}`}
      >
        <div className="text-white font-bold text-sm">
          <span className="text-white">{s.id}</span>{" "}
          <span className="text-[var(--gl-gold)]">{s.title}</span>
        </div>
        {(s.composer || s.artist) && (
          <div className="text-[var(--gl-green)] text-xs truncate">
            {s.composer || s.artist}
          </div>
        )}
        <div
          className="text-[10px] text-neutral-400 tabular-nums truncate"
          data-testid={`setlist-row-meta-${s.id}`}
        >
          {s.bpm ? `${Math.round(s.bpm)} BPM` : "— BPM"} · {s.beat || "4/4"}
        </div>
      </button>
      <div className="flex items-center gap-1 shrink-0">
        {(s.audio_playback_filename || s.audio_repete_filename || s.audio_filename) && (
          <Music size={12} className="text-[var(--gl-gold)]" />
        )}
        {s.pdf_filename && <FileText size={12} className="text-white/70" />}
      </div>
      <span
        className={`text-[10px] font-bold tabular-nums rounded px-1.5 py-0.5 border shrink-0 ${
          isManualDuration
            ? "text-[var(--gl-gold)] bg-[var(--gl-gold)]/15 border-[var(--gl-gold)]/40"
            : "text-white/70 bg-white/5 border-white/10"
        }`}
        title={isManualDuration ? "Durée manuelle" : "Durée auto-détectée"}
      >
        {durationSec > 0 ? formatDuration(durationSec) : "--:--"}
      </span>
      <div className="flex flex-col">
        <button
          onClick={() => onMoveUp?.(s.id)}
          disabled={index === 0}
          className="w-7 h-7 rounded bg-[#1a1a1a] border border-white/10 flex items-center justify-center disabled:opacity-30 hover:bg-white/5"
          data-testid={`setlist-up-${s.id}`}
        >
          <ChevronUp size={14} />
        </button>
        <button
          onClick={() => onMoveDown?.(s.id)}
          disabled={index === total - 1}
          className="w-7 h-7 rounded bg-[#1a1a1a] border border-white/10 flex items-center justify-center disabled:opacity-30 hover:bg-white/5 mt-1"
          data-testid={`setlist-down-${s.id}`}
        >
          <ChevronDown size={14} />
        </button>
      </div>
      <button
        onClick={() => onRemove?.(s.id)}
        className="w-7 h-7 rounded bg-[#1a1a1a] border border-white/10 flex items-center justify-center hover:bg-orange-500/20 hover:border-orange-500/50 hover:text-orange-400"
        data-testid={`setlist-remove-${s.id}`}
      >
        <X size={14} />
      </button>
    </li>
  );
};

const SetlistChip = ({ sl, isActive, onActivate, onRename }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(sl.name);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing) {
      setDraft(sl.name);
      // focus + select on next tick
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing, sl.name]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== sl.name) onRename?.(sl.id, trimmed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(sl.name);
    setEditing(false);
  };

  const handleDoubleClick = () => {
    if (!isActive) onActivate?.(sl.id);
    setEditing(true);
  };

  const songCount = (sl.song_ids || []).length;

  if (editing) {
    return (
      <div
        className={`shrink-0 rounded-lg border-2 px-2 py-1.5 flex items-center gap-1 ${
          isActive
            ? "border-[var(--gl-gold)] bg-[var(--gl-gold)]/10"
            : "border-white/15 bg-black"
        }`}
        data-testid={`setlist-chip-${sl.id}`}
      >
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          className="bg-transparent text-sm font-bold text-[var(--gl-gold)] focus:outline-none w-[140px]"
          maxLength={60}
          data-testid={`setlist-rename-input-${sl.id}`}
        />
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          className="text-[var(--gl-green)] hover:text-white shrink-0"
          aria-label="Valider"
          data-testid={`setlist-rename-confirm-${sl.id}`}
        >
          <Check size={14} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => onActivate?.(sl.id)}
      onDoubleClick={handleDoubleClick}
      className={`shrink-0 rounded-lg border-2 px-3 py-2 text-left transition relative group ${
        isActive
          ? "border-[var(--gl-gold)] bg-[var(--gl-gold)]/10"
          : "border-white/15 bg-black hover:border-white/30"
      }`}
      data-testid={`setlist-chip-${sl.id}`}
      title="Tape pour activer · double-tap pour renommer"
    >
      <div className={`flex items-center gap-1.5`}>
        <div className={`text-sm font-bold truncate max-w-[120px] ${isActive ? "text-[var(--gl-gold)]" : "text-white"}`}>
          {sl.name}
        </div>
        {isActive && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setEditing(true); } }}
            className="text-[var(--gl-gold)]/60 hover:text-[var(--gl-gold)] shrink-0 cursor-pointer"
            aria-label="Renommer"
            data-testid={`setlist-rename-btn-${sl.id}`}
          >
            <Pencil size={11} />
          </span>
        )}
      </div>
      <div className="text-[10px] text-neutral-400 tabular-nums">
        {songCount} morceau{songCount > 1 ? "x" : ""}
      </div>
    </button>
  );
};

export const SetlistDialog = ({
  open,
  onOpenChange,
  setlist,
  setlists = [],
  activeId,
  songs,
  onAdd,
  onRemove,
  onMoveUp,
  onMoveDown,
  onReorder,
  onRename,
  onClear,
  onJump,
  onCreateSetlist,
  onDeleteSetlist,
  onDuplicateSetlist,
  onActivateSetlist,
  onRenameSetlist,
}) => {
  const songIds = setlist?.song_ids || [];
  const setlistSongs = songIds
    .map((id) => songs.find((s) => s.id === id))
    .filter(Boolean);
  const setlistIds = new Set(songIds);
  const availableSongs = songs.filter((s) => !setlistIds.has(s.id));

  const totalSongs = setlistSongs.length;
  const totalSetlists = setlists.length;

  // Duration probing: prefer playback variant, fallback to repete then legacy
  const audioFilenames = React.useMemo(
    () =>
      setlistSongs
        .map(
          (s) => s.audio_playback_filename || s.audio_repete_filename || s.audio_filename
        )
        .filter(Boolean),
    [setlistSongs]
  );
  const durations = useAudioDurations(audioFilenames);
  // Total uses manual duration when present, otherwise probed.
  const totalSeconds = setlistSongs.reduce(
    (sum, s) => sum + resolveSongDuration(s, durations),
    0
  );
  const missingAudio = setlistSongs.filter(
    (s) => resolveSongDuration(s, durations) === 0
  ).length;
  const loadingDurations =
    audioFilenames.some((f) => durations[f] === undefined);

  const sensors = useSensors(
    // 6px on desktop -> avoid stealing clicks from "jump"
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // 180ms hold on touch -> allows scroll, then drag with explicit intent
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = songIds.indexOf(active.id);
    const newIndex = songIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(songIds, oldIndex, newIndex);
    onReorder?.(next);
  };

  const handleCreate = () => {
    const name = window.prompt("Nom de la nouvelle setlist ?", "Nouvelle setlist");
    if (name && name.trim()) onCreateSetlist?.(name.trim());
  };

  const handleDelete = () => {
    if (totalSetlists <= 1) return;
    if (window.confirm(`Supprimer la setlist « ${setlist?.name} » ?`)) {
      onDeleteSetlist?.(activeId);
    }
  };

  const handleDuplicate = () => {
    if (!activeId) return;
    const suggested = `${setlist?.name || "Setlist"} (copie)`;
    const name = window.prompt("Nom de la nouvelle setlist ?", suggested);
    if (name !== null) {
      // null = user cancelled. Empty string or trimmed name: server fallback applies.
      onDuplicateSetlist?.(activeId, name.trim() || undefined);
    }
  };

  const handleExportPdf = () => {
    if (!activeId) return;
    const token = typeof window !== "undefined"
      ? window.localStorage.getItem("gl26_auth_token")
      : null;
    if (!token) return;
    // Pass the durations to use in the PDF. Manual override wins; fallback to probed.
    // Format: "A001:215.3,A002:191.7,…"
    const pairs = [];
    for (const s of setlistSongs) {
      const d = resolveSongDuration(s, durations);
      if (d > 0) pairs.push(`${s.id}:${d.toFixed(1)}`);
    }
    const params = new URLSearchParams();
    params.set("t", token);
    if (pairs.length > 0) params.set("durations", pairs.join(","));
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/setlists/${activeId}/export.pdf?${params.toString()}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0d0d0d] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto no-scrollbar"
        data-testid="setlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-[var(--gl-gold)] font-display">
            Setlist · {totalSongs} morceau{totalSongs > 1 ? "x" : ""}
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Construis l'ordre du soir. Glisse la poignée <span className="inline-flex items-center align-middle text-[var(--gl-gold)]"><GripVertical size={12} /></span> pour réordonner.
          </DialogDescription>
        </DialogHeader>

        {/* Total duration banner */}
        {totalSongs > 0 && (
          <div
            className="bg-black border border-[var(--gl-gold)]/30 rounded-xl px-3 py-2 flex items-center gap-3"
            data-testid="setlist-duration-banner"
          >
            <Clock size={16} className="text-[var(--gl-gold)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
                Durée totale {loadingDurations ? "(estimation…)" : "estimée"}
              </div>
              <div className="text-xl font-display text-[var(--gl-gold)] tabular-nums leading-tight">
                {totalSeconds > 0 ? "≈ " : ""}{formatDuration(totalSeconds)}
              </div>
            </div>
            {missingAudio > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
                  Sans audio
                </div>
                <div className="text-base font-bold text-orange-400 tabular-nums leading-tight">
                  {missingAudio}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Multi-setlist switcher */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">
              Mes setlists ({totalSetlists})
            </div>
            <button
              onClick={handleCreate}
              className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] hover:text-white transition flex items-center gap-1"
              data-testid="setlist-create-btn"
            >
              <ListPlus size={12} /> Nouvelle
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
            {setlists.map((sl) => (
              <SetlistChip
                key={sl.id}
                sl={sl}
                isActive={sl.id === activeId}
                onActivate={onActivateSetlist}
                onRename={onRenameSetlist}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold">
              Nom de la setlist active
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={handleExportPdf}
                disabled={totalSongs === 0}
                className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[var(--gl-gold)] disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                data-testid="setlist-export-pdf-btn"
                title="Télécharger la setlist en PDF"
              >
                <Download size={11} /> PDF
              </button>
              <button
                onClick={handleDuplicate}
                className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[var(--gl-gold)] transition flex items-center gap-1"
                data-testid="setlist-duplicate-btn"
                title="Dupliquer cette setlist"
              >
                <Copy size={11} /> Dupliquer
              </button>
              {totalSetlists > 1 && (
                <button
                  onClick={handleDelete}
                  className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-orange-400 transition flex items-center gap-1"
                  data-testid="setlist-delete-btn"
                  title="Supprimer cette setlist"
                >
                  <Trash2 size={11} /> Supprimer
                </button>
              )}
            </div>
          </div>
          <input
            value={setlist?.name || ""}
            onChange={(e) => onRename?.(e.target.value)}
            placeholder="Concert du soir"
            className="w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-base font-bold text-white focus:outline-none focus:border-[var(--gl-gold)]"
            data-testid="setlist-name-input"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] font-bold">
              Ordre du soir
            </div>
            {totalSongs > 0 && (
              <button
                onClick={onClear}
                className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-orange-400 transition flex items-center gap-1"
                data-testid="setlist-clear-btn"
              >
                <Trash2 size={11} /> Vider
              </button>
            )}
          </div>
          {totalSongs === 0 ? (
            <div className="text-center text-neutral-500 text-sm py-6 border border-dashed border-white/10 rounded-xl">
              Setlist vide. Ajoute des morceaux ci-dessous.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={songIds} strategy={verticalListSortingStrategy}>
                <ul className="space-y-2" data-testid="setlist-sortable-list">
                  {setlistSongs.map((s, i) => (
                    <SortableSetlistItem
                      key={s.id}
                      s={s}
                      index={i}
                      total={totalSongs}
                      durationSec={resolveSongDuration(s, durations)}
                      isManualDuration={!!(s.duration_seconds && s.duration_seconds > 0)}
                      onJump={onJump}
                      onMoveUp={onMoveUp}
                      onMoveDown={onMoveDown}
                      onRemove={onRemove}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {availableSongs.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">
              Morceaux disponibles
            </div>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto no-scrollbar">
              {availableSongs.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => onAdd?.(s.id)}
                    className="w-full text-left p-2.5 rounded-lg bg-black border border-white/10 hover:border-[var(--gl-gold)]/50 flex items-center gap-3 transition"
                    data-testid={`setlist-add-${s.id}`}
                  >
                    <Plus size={16} className="text-[var(--gl-gold)] shrink-0" />
                    <span className="text-white font-bold text-sm">{s.id}</span>
                    <span className="text-[var(--gl-gold)] text-sm truncate flex-1">{s.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SetlistDialog;
